import { inject, Service } from '@angular/core';

import { TroubleshootingLogsProvider } from '@ideals/help';
import { IndexedDbService } from '@ideals/services/indexed-db';

import { ErrorLog } from './local-error-logger';

interface Record {
  readonly key: string;
  readonly value: ErrorLog;
}

const STORE_NAME = 'error-logs';
const THROTTLE_INTERVAL = 15_000;
const MAX_DUPLICATE_COUNT = 10;
const MAX_RECORDS = 500;
const CACHE_SIZE = 20;

@Service()
export class LocalErrorLogsService implements TroubleshootingLogsProvider {
  readonly #indexedDbService = inject(IndexedDbService);

  readonly #errorCache = new Map<string, number>();

  available(): boolean {
    return this.#indexedDbService.available();
  }

  async clearLogs(): Promise<void> {
    this.#errorCache.clear();
    await this.#indexedDbService.removeItem(STORE_NAME);
  }

  async getLogs(): Promise<ErrorLog[]> {
    const records = await this.#indexedDbService.getItem<Record[] | null>(STORE_NAME) ?? [];

    return records.map((record) => record.value);
  }

  async saveLog(path: string, value: ErrorLog): Promise<void> {
    const now = Date.now();
    const key = this.#getHash(path);
    const lastOccurred = this.#errorCache.get(key);

    if (lastOccurred && now - lastOccurred < THROTTLE_INTERVAL) {
      return;
    }

    this.#errorCache.set(key, now);

    const records = await this.#indexedDbService.getItem<Record[] | null>(STORE_NAME) ?? [];
    const updatedRecords = this.#upsert(key, value, records);

    await this.#indexedDbService.setItem<Record[]>(STORE_NAME, updatedRecords.slice(-MAX_RECORDS));

    if (this.#errorCache.size > CACHE_SIZE) {
      this.#cleanupCache(now);
    }
  }

  #cleanupCache(now: number): void {
    const cutoff = now - THROTTLE_INTERVAL;

    for (const [key, time] of this.#errorCache.entries()) {
      if (time < cutoff) {
        this.#errorCache.delete(key);
      }
    }
  }

  #getHash(input: string): string {
    const prime = 0x01000193;
    const radix = 16;
    const length = 8;
    let hash = 0x811c9dc5;

    for (let i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, prime);
    }

    return (hash >>> 0).toString(radix).padStart(length, '0');
  }

  #upsert(key: string, value: ErrorLog, records: Record[]): Record[] {
    const index = records.findIndex((r) => r.key === key && r.value.count < MAX_DUPLICATE_COUNT);

    const record = index >= 0
      ? {
        key,
        value: {
          ...records[index].value,
          count: records[index].value.count + 1,
          lastOccurred: value.date,
        },
      }
      : { key, value };

    return index >= 0
      ? [
        ...records.slice(0, index),
        record,
        ...records.slice(index + 1),
      ]
      : [...records, record];
  }
}
